<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useGamesStore } from '@/stores/games'
import { storeToRefs } from 'pinia'
import MultiselectField from '@/components/Form/MultiselectField.vue'
import BasicButton from '@/components/Buttons/BasicButton.vue'
import FunPlayModal from './Partials/FunPlayModal.vue'
const assetsUrl = import.meta.env.VITE_ASSETS_URL
const route = useRoute()
const router = useRouter()
const gamesStore = useGamesStore()
const auth = useAuthStore()

const { getCategory, getSubcategories, getGames, getProvidersByCategory, getGame } = storeToRefs(gamesStore)
const { getCredits, getCurrency, getUser, isAuth } = storeToRefs(auth)

const categoryId = ref(route.params.id)
const selectedSubcategory = ref(null)
const selectedProviders = ref([])
const page = ref(1)
const pageSize = 21
const isLoadingMore = ref(false)

const selectedGame = ref(null)
const modalRef = ref(null)
const lang = ref(localStorage.getItem('lang') || 'en')

const openGame = (game) => {
  if (isAuth.value) {
    if (game.has_demo === 'Y' && getUser.value?.c_p_demo) {
      selectedGame.value = game
      modalRef.value.openModal()
    } else {
      launchGame(game, false)
    }
  } else {
    router.push({ name: 'login' })
  }
}

const launchGame = async (game, isDemo) => {
  try {
    const res = await gamesStore.openGame(game._id, {
      device: 'M',
      currency_id: getCredits.value[0].cuid._id,
      currency_name: getCurrency.value(getCredits.value[0].cuid._id).cod,
      homeURL: import.meta.env.VITE_APP_URL,
      demo: isDemo,
      lang: lang.value
    });

    if (res.data?.nt === true) {
      window.open(res.data.data, '_blank');
      return;
    }

    if (res.data?.nt === false) {
      gamesStore.setGameUrl(res.data.data);
      router.push('/casino/game');
    }
  } catch (error) {
    console.error(error);
  }
};

const handlePlay = ({ demo }) => {
  launchGame(selectedGame.value, demo)
}

const filteredGames = computed(() =>
  getGames.value(
    categoryId.value,
    selectedSubcategory.value?._id,
    selectedProviders.value
  )
)

const visibleGames = computed(() =>
  filteredGames.value.slice(0, page.value * pageSize)
)

const hasMore = computed(() =>
  filteredGames.value.length > page.value * pageSize
)

async function showMore() {
  if (isLoadingMore.value) return
  isLoadingMore.value = true
  const start = page.value * pageSize
  const nextBatch = filteredGames.value.slice(start, start + pageSize)

  await Promise.all(
    nextBatch.map(game => new Promise(resolve => {
      const img = new Image()
      img.src = game.img ?? game.extimg
      img.onload = () => resolve()
      img.onerror = () => resolve()
    }))
  )

  page.value++
  isLoadingMore.value = false
}

watch([categoryId, selectedSubcategory, selectedProviders], () => {
  page.value = 1
})


onMounted(() => {
  const subs = getSubcategories.value(categoryId.value)
  if (subs?.length) {
    selectedSubcategory.value = subs[0]
  }
})
</script>

<template>
  <div class="px-3">
    <!-- Category name -->
    <div class="flex items-center justify-between gap-5 py-2">
      <h3 class="text-gray-300 text-xl font-bold">
        {{ getCategory(categoryId)?.nm }}
      </h3>
    </div>

    <!-- Subcategories & Providers -->
    <div class="flex items-center justify-between gap-5 mb-6">
      <!-- subcategories -->
      <div class="flex items-center overflow-x-auto space-x-4">
        <div
          v-for="sub in getSubcategories(categoryId)"
          :key="sub._id"
          class="py-2 border-b-2 border-transparent cursor-pointer"
          :class="{
            '!border-[var(--color-primary4)]': selectedSubcategory?._id == sub._id
          }"
          @click="selectedSubcategory = sub"
        >
          <p
            class="text-[11px] font-bold"
            :class="[
              selectedSubcategory?._id == sub._id
                ? 'text-[var(--color-primary4)]'
                : 'text-neutral-400'
            ]"
          >
            {{ sub.nm }}
          </p>
        </div>
      </div>

      <!-- provider multiselect -->
      <div class="max-w-[150px]">
        <MultiselectField
          :options="getProvidersByCategory(categoryId).sort((a, b) => a.ord - b.ord)"
          value-attribute="_id"
          text-attribute="nm"
          v-model="selectedProviders"
          placeholder="Providers"
        />
      </div>
    </div>

    <!-- games list -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-6">
      <template v-for="game in visibleGames" :key="game._id">
        <div class="cursor-pointer" @click="openGame(game)">
          <img
            v-img-fallback="[`${assetsUrl}/c/g/old/${game.img}`, `${assetsUrl}/c/g/${game.img}`, game.extimg]"   
            class="game-thumb rounded-xl"
            alt=""
            loading="lazy"
          />  
        </div>
      </template>
    </div>

    <!-- show more -->
    <div class="mt-4 flex justify-center" v-if="hasMore">
      <BasicButton
        @click="showMore"
        :disabled="isLoadingMore"
        :loading="isLoadingMore"
        class="text-sm text-white font-medium shadow-md bg-[var(--color-primary1)] 
          rounded-md py-2 px-4 flex items-center space-x-2"
      >
        <span v-if="!isLoadingMore">Show more</span>
      </BasicButton>
    </div>

    <FunPlayModal ref="modalRef" :value="selectedGame" @play="handlePlay" />
  </div>
</template>

<style scoped>
.game-thumb {
  width: 100%;
  height: auto;
  object-fit: cover;
}
</style>
